import { ArrowRight, Calendar } from 'lucide-react';

const Hero = () => {
  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      id="hero" 
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background Effects */}
      <div className="absolute inset-0 bg-slate-950">
        {/* Mesh gradient */}
        <div className="absolute inset-0 mesh-gradient" />
        
        {/* Grid overlay */}
        <div className="absolute inset-0 grid-bg opacity-50" />
        
        {/* Radial gradient at top */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] radial-gradient" />
        
        {/* Animated orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-[120px] animate-float" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-indigo-500/20 rounded-full blur-[100px] animate-float" style={{ animationDelay: '-3s' }} />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 text-center">
        {/* Badge */}
        <div 
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-700 bg-slate-900/50 backdrop-blur-sm mb-8 opacity-0 animate-fade-in-up"
          data-testid="hero-badge"
        >
          <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
          <span className="text-sm text-slate-400">Software Development & Consulting</span>
        </div>

        {/* Headline */}
        <h1 
          className="font-space text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.1] mb-6 opacity-0 animate-fade-in-up stagger-1"
          data-testid="hero-headline"
        >
          Building Scalable Digital
          <br />
          <span className="text-gradient-blue">Solutions</span> for Modern
          <br />
          Businesses
        </h1>

        {/* Subheading */}
        <p 
          className="text-base md:text-lg text-slate-400 max-w-2xl mx-auto mb-10 leading-relaxed opacity-0 animate-fade-in-up stagger-2"
          data-testid="hero-subheadline"
        >
          We design, develop, and deliver high-performance web and mobile applications 
          that scale with your business and exceed user expectations.
        </p>

        {/* CTA Buttons */}
        <div 
          className="flex flex-col sm:flex-row items-center justify-center gap-4 opacity-0 animate-fade-in-up stagger-3"
          data-testid="hero-cta-container"
        >
          <button
            onClick={scrollToContact}
            className="group flex items-center gap-2 px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-full font-medium transition-all duration-300 hover:shadow-[0_0_30px_rgba(59,130,246,0.5)] active:scale-95"
            data-testid="hero-get-started-btn"
          >
            Get Started
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </button>
          <button
            onClick={scrollToContact}
            className="flex items-center gap-2 px-8 py-4 bg-transparent border border-slate-700 hover:border-blue-500/50 text-slate-300 hover:text-white rounded-full font-medium transition-all duration-300 hover:bg-blue-500/10"
            data-testid="hero-book-call-btn"
          >
            <Calendar size={18} />
            Book a Call
          </button>
        </div>

        {/* Stats */}
        <div 
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 opacity-0 animate-fade-in-up stagger-4"
          data-testid="hero-stats"
        >
          {[
            { value: '6+', label: 'Years Experience' },
            { value: '50+', label: 'Projects Delivered' },
            { value: '30+', label: 'Happy Clients' },
            { value: '99%', label: 'Client Satisfaction' },
          ].map((stat, index) => (
            <div key={index} className="text-center">
              <div className="font-space text-3xl md:text-4xl font-bold text-white mb-1">
                {stat.value}
              </div>
              <div className="text-sm text-slate-500">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-0 animate-fade-in stagger-5">
        <span className="text-xs text-slate-500 uppercase tracking-widest">Scroll</span>
        <div className="w-5 h-8 rounded-full border border-slate-700 flex items-start justify-center p-1">
          <div className="w-1 h-2 rounded-full bg-blue-500 animate-bounce" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
